// ST-1281/ST-1287 -Claude- Location resolver hook for Nearrme web.
// Sources: user text/PIN, browser GPS, and a non-blocking IP city fallback.

import { useEffect, useState } from 'react';

export interface NearrmeLocation {
  lat: number;
  lng: number;
  displayName: string;
  source: 'user-provided' | 'gps' | 'pincode' | 'ip-fallback';
}

const STORAGE_KEY = 'nearrme.location';
const LOCATION_EVENT = 'nearrme.location.changed';
const IP_FALLBACK_ATTEMPTED_KEY = 'nearrme.location.ipFallbackAttempted';

function load(): NearrmeLocation | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NearrmeLocation) : null;
  } catch {
    return null;
  }
}

function persist(loc: NearrmeLocation | null) {
  if (typeof window === 'undefined') return;
  if (loc) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent(LOCATION_EVENT, { detail: loc }));
}

export function useLocation() {
  const [location, setLocationState] = useState<NearrmeLocation | null>(load);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setLocation = (loc: NearrmeLocation | null) => {
    setLocationState(loc);
    persist(loc);
  };

  const requestGeolocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          displayName: 'My location',
          source: 'gps',
        });
        setLoading(false);
      },
      (err) => {
        console.warn('[useLocation.requestGeolocation] denied:', err);
        setError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied. Try entering a city or PIN code instead.'
            : 'Could not determine your location.'
        );
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  };

  const lookupByText = async (query: string): Promise<NearrmeLocation | null> => {
    if (!query || query.trim().length < 2) {
      setError('Type at least 2 characters.');
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        format: 'json',
        limit: '1',
        countrycodes: 'in',
        addressdetails: '1',
      });
      const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        throw new Error(`Geocoder returned HTTP ${res.status}`);
      }
      const arr = (await res.json()) as Array<{
        lat: string;
        lon: string;
        display_name: string;
      }>;
      if (arr.length === 0) {
        setError(`Couldn't find "${query}". Try a city, address, or 6-digit PIN code.`);
        setLoading(false);
        return null;
      }
      const isPincode = /^\d{6}$/.test(query.trim());
      const loc: NearrmeLocation = {
        lat: parseFloat(arr[0].lat),
        lng: parseFloat(arr[0].lon),
        displayName: arr[0].display_name,
        source: isPincode ? 'pincode' : 'user-provided',
      };
      setLocation(loc);
      setLoading(false);
      return loc;
    } catch (e: any) {
      console.error('[useLocation.lookupByText]', e);
      setError(e?.message ?? 'Location lookup failed.');
      setLoading(false);
      return null;
    }
  };

  const resolveIpFallback = async () => {
    if (typeof window === 'undefined') return;
    if (load()) return;
    if (sessionStorage.getItem(IP_FALLBACK_ATTEMPTED_KEY) === '1') return;

    sessionStorage.setItem(IP_FALLBACK_ATTEMPTED_KEY, '1');
    try {
      const res = await fetch('https://ipapi.co/json/', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        throw new Error(`IP location lookup returned HTTP ${res.status}`);
      }
      const data = (await res.json()) as {
        city?: string;
        region?: string;
        country_name?: string;
        latitude?: number;
        longitude?: number;
      };
      if (!data.city || typeof data.latitude !== 'number' || typeof data.longitude !== 'number') {
        throw new Error('IP location lookup did not return city coordinates.');
      }
      setLocation({
        lat: data.latitude,
        lng: data.longitude,
        displayName: [data.city, data.region, data.country_name].filter(Boolean).join(', '),
        source: 'ip-fallback',
      });
    } catch (e) {
      console.warn('[useLocation.resolveIpFallback]', e);
    }
  };

  const clearLocation = () => setLocation(null);

  useEffect(() => {
    const syncFromStorage = () => setLocationState(load());
    window.addEventListener(LOCATION_EVENT, syncFromStorage);
    window.addEventListener('storage', syncFromStorage);
    void resolveIpFallback();
    return () => {
      window.removeEventListener(LOCATION_EVENT, syncFromStorage);
      window.removeEventListener('storage', syncFromStorage);
    };
  }, []);

  return {
    location,
    loading,
    error,
    setLocation,
    requestGeolocation,
    lookupByText,
    clearLocation,
  };
}
